import React, { useEffect, useState } from "react";
import { Form, Button, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import FormInput from "../../../components/FormInputs/FormInput";
import FormTextarea from "../../../components/FormInputs/FormTextarea";
import { postApi, getApi } from "../../../utils/apiServices";
import "./AddBlog.scss";
import {
  ADD_BLOG_API,
  GET_BLOG_BY_ID,
  UPDATE_BLOG,
  GET_NESTED_CATEGORY_DROPDOWN,
} from "../../../utils/apiPath";
import ToastHelper from "../../../services/ToastHelper";
import FormSelect from "../../../components/FormInputs/FormSelect";
import { IMAGE_BASE_PATH } from "../../../utils/constant";
import UploadWithCrop from "../../../components/FormInputs/UploadInput";

export default function AddBlog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEditMode] = useState(!!id);
  const [categories, setCategories] = useState([]);
  const [blogData, setBlogData] = useState(null);
  const [base64, setBase64] = useState(null);
  const [image, setImage] = useState(null);

  useEffect(() => {
    fetchCategories();
    if (isEditMode) {
      getBlogDetails();
    }
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await getApi(GET_NESTED_CATEGORY_DROPDOWN);

      const flatCategories = [];

      const flatten = (categories) => {
        categories.forEach((category) => {
          flatCategories.push({
            label: category.categoryName,
            value: category.categoryId,
          });
          if (category.categories && category.categories.length > 0) {
            flatten(category.categories);
          }
        });
      };

      flatten(res?.data || []);
      setCategories(flatCategories);
    } catch (error) {
      console.error("Error fetching categories", error);
    }
  };

  const getBlogDetails = async () => {
    try {
      setLoading(true);
      const res = await getApi(`${GET_BLOG_BY_ID}?blogId=${id}`);
      setBlogData(res?.data);
    } catch (error) {
      ToastHelper.error("Error fetching blog data");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode && blogData) {
      setImage(`${IMAGE_BASE_PATH}${blogData?.media?.url}`);
      const initialValues = {
        name: blogData?.name,
        categoryId: blogData?.categoryId,
        description: blogData?.description,
        metaTitle: blogData?.metaTitle || "",
        metaDescription: blogData?.metaDescription || "",
        urlName: blogData?.urlName,
        schema: JSON.stringify(blogData?.schema),
        altText: blogData?.media?.altText || "",
        image: blogData?.media?.media
      };

      form.setFieldsValue(initialValues);
    }
  }, [isEditMode, blogData]);

  const handleUpload = (croppedBlob) => {
    setBase64(croppedBlob);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      let payload = {
        name: values?.name,
        description: values?.description,
        urlName: values?.urlName,
        categoryId: values.categoryId || null,
        metaTitle: values.metaTitle || "",
        metaDescription: values.metaDescription || "",
        schema: values.schema ? JSON.parse(values.schema) : [],
        media: (isEditMode && !base64) ? blogData?.media : { media: base64?.split(",")[1], altText: values?.altText },
      };

      if (isEditMode) {
        payload.id = parseInt(id);
      }

      const apiEndpoint = isEditMode ? UPDATE_BLOG : ADD_BLOG_API;
      const response = await postApi(apiEndpoint, payload);

      if (response.statusCode === 200) {
        ToastHelper.success(
          `Blog ${isEditMode ? "updated" : "created"} successfully`
        );
        navigate("/manage/blogs");
      } else {
        ToastHelper.error(response.message || "Operation failed");
      }
    } catch (error) {
      ToastHelper.error("An error occurred");
      console.error("Submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-blog">
      <div className="add-blog_heading">
        <h1>{isEditMode ? "Edit Blog" : "Add New Blog"}</h1>
      </div>

      <div className="add-blog_container">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="blog-form"
        >
          <div className="form-grid">
            <div className="form-row">
              <FormInput
                label="Blog Title"
                name="name"
                rules={[{ required: true, message: "Please enter blog title" }]}
                placeholder="Enter blog title"
              />

              <FormSelect
                label="Category"
                name="categoryId"
                options={categories}
                placeholder="Select Category"
                rules={[
                  { required: true, message: "Please select a category" },
                ]}
              />
            </div>

            <div className="form-row">
              <FormTextarea
                label="Description"
                name="description"
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
                placeholder="Enter blog description"
                rows={6}
              />
            </div>

            <div className="form-row">
              <FormInput
                label="Meta Title"
                name="metaTitle"
                placeholder="Enter meta title for SEO (optional)"
              />

              <FormTextarea
                label="Meta Description"
                name="metaDescription"
                placeholder="Enter meta description for SEO (optional)"
                rows={3}
              />
            </div>

            <div className="form-row">
              <FormInput
                label="URL Slug"
                name="urlName"
                rules={[{ required: true, message: "Please enter URL slug" }]}
                placeholder="Enter URL slug (e.g., my-blog-post)"
              />
            </div>

            <div className="form-row">
              <UploadWithCrop
                onUpload={handleUpload}
                defaultAspect={3 / 2}
                image={image}
                required={true}
                form={form}
              />
            </div>

            <div className="form-row">
              <FormTextarea
                label="Schema Markup (JSON)"
                name="schema"
                placeholder={`Enter schema markup in JSON format, e.g.:\n[\n  {\n    "type": "string",\n    "content": "string"\n  }\n]`}
                rows={6}
              />
            </div>
          </div>

          <div className="form-actions">
            <Button
              type="default"
              onClick={() => navigate(-1)}
              style={{ width: "150px", marginRight: "16px" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ width: "150px" }}
            >
              {isEditMode ? "Update" : "Submit"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
